import { useMemo } from 'react';
import { PanelDefinition } from '@/core';
import { usePlugins } from '../plugin-registry';
import { getInjectVariableFunction, InitializedDataQueryPlugin, initializeDataQueryPlugin } from './plugin-utils';

export interface PluginCapabilities {
  pluginSupportsInjection: Map<string, boolean>;
  isLoadingPlugins: boolean;
  initializedPlugins: Map<string, InitializedDataQueryPlugin>;
}

/**
 * Hook to load plugins and check their variable injection capabilities.
 * Extracts unique plugin kinds from panels and initializes them.
 */
export function usePluginCapabilities(panels: Record<string, PanelDefinition>): PluginCapabilities {
  // Extract unique plugin kinds from all panels to check their capabilities
  const pluginKinds = useMemo(() => {
    const kinds = new Set<string>();
    Object.values(panels).forEach((panel) => {
      panel.spec.queries?.forEach((queryDef) => {
        const kind = queryDef.spec?.plugin?.kind;
        if (kind) {
          kinds.add(kind);
        }
      });
    });
    // Sort for stable ordering to ensure pluginsQueryResults aligns by index
    return Array.from(kinds)
      .sort()
      .map((kind) => ({ kind }));
  }, [panels]);

  // Load all plugins to check their capabilities
  const pluginsQueryResults = usePlugins('DataQuery', pluginKinds);

  // Build a map of which plugin kinds support variable injection and initialize plugins
  const capabilities = useMemo(() => {
    const supportMap = new Map<string, boolean>();
    const pluginMap = new Map<string, InitializedDataQueryPlugin>();
    let stillLoading = false;

    pluginKinds.forEach(({ kind }, index) => {
      const queryResult = pluginsQueryResults[index];
      if (queryResult?.isLoading) {
        stillLoading = true;
      } else if (queryResult?.data) {
        const plugin = initializeDataQueryPlugin(queryResult.data);
        pluginMap.set(kind, plugin);
        const injectVariableFn = getInjectVariableFunction(plugin);
        supportMap.set(kind, injectVariableFn !== undefined);
      } else {
        // Plugin failed to load or doesn't exist - mark as not supported
        supportMap.set(kind, false);
      }
    });

    return {
      pluginSupportsInjection: supportMap,
      isLoadingPlugins: stillLoading,
      initializedPlugins: pluginMap,
    };
  }, [pluginsQueryResults, pluginKinds]);

  return capabilities;
}
